import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { enrollCourseApi, getMyEnrollmentsApi, completeLectureApi } from '../api/apiServices';

const ProgressContext = createContext(null);
const ENROLLMENTS_KEY = 'learnflow_enrollments';

// Helper to normalize any ID representation to a trimmed string
const normalizeId = (id) => {
  if (!id) return '';
  if (typeof id === 'object') {
    return String(id._id || id.id || id.userId || id.courseId || '').trim();
  }
  return String(id).trim();
};

export function ProgressProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);

  const saveToLocal = useCallback((data) => {
    setEnrollments(data);
    try {
      localStorage.setItem(ENROLLMENTS_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn("Could not save enrollments to localStorage:", e);
    }
  }, []);

  // Fetch enrollments from backend & sync with local storage
  const refreshEnrollments = useCallback(async () => {
    if (!user || !isAuthenticated) return;
    try {
      setLoading(true);
      const res = await getMyEnrollmentsApi();
      if (res && res.success && Array.isArray(res.enrollments)) {
        saveToLocal(res.enrollments);
      }
    } catch (err) {
      console.warn("Failed to sync enrollments from backend:", err);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated, saveToLocal]);

  // Load enrollments on mount or when auth state changes
  useEffect(() => {
    // 1. First load from localStorage for instant display
    const stored = localStorage.getItem(ENROLLMENTS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setEnrollments(parsed);
        }
      } catch (e) {
        console.error("Failed to parse local enrollments");
      }
    }

    // 2. Fetch fresh data from backend
    if (user && isAuthenticated) {
      refreshEnrollments();
    }
  }, [user, isAuthenticated, refreshEnrollments]);

  const getEnrollment = useCallback((courseId) => {
    if (!courseId) return null;
    const targetCourseId = normalizeId(courseId);
    const currentUserId = normalizeId(user?.id || user?._id || user?.userId);

    return enrollments.find(e => {
      const storedCourseId = normalizeId(e.courseId);
      const storedUserId = normalizeId(e.userId || e.user);
      
      if (currentUserId && storedUserId) {
        return storedCourseId === targetCourseId && storedUserId === currentUserId;
      }
      return storedCourseId === targetCourseId;
    }) || null;
  }, [enrollments, user]);

  const isEnrolled = useCallback((courseId) => {
    return !!getEnrollment(courseId);
  }, [getEnrollment]);

  const enroll = useCallback(async (courseId) => {
    if (!user || !isAuthenticated) return;
    const targetCourseId = normalizeId(courseId);
    if (!targetCourseId) return;

    try {
      const result = await enrollCourseApi(targetCourseId);

      if (result && result.success) {
        const newEnrollment = {
          courseId: targetCourseId,
          userId: normalizeId(user.id || user._id || user.userId),
          enrolledAt: new Date().toISOString(),
          completedLessons: [],
          quizResults: [],
          courseCompleted: false,
          lastLessonId: null,
          ...(result.enrollment || {}),
        };

        const existingFiltered = enrollments.filter(e => normalizeId(e.courseId) !== targetCourseId);
        saveToLocal([...existingFiltered, newEnrollment]);
      }
    } catch (err) {
      console.error("Enroll failed:", err);
      // If backend says already enrolled, refresh
      if (err.response?.data?.message?.toLowerCase().includes("already enrolled")) {
        await refreshEnrollments();
      }
      throw err;
    }
  }, [user, isAuthenticated, enrollments, saveToLocal, refreshEnrollments]);

  // Complete a lecture / lesson
  const completeLesson = useCallback(async (courseId, lessonId) => {
    if (!courseId || !lessonId) return;
    const targetCourseId = normalizeId(courseId);
    const targetLessonId = normalizeId(lessonId);
    const currentUserId = normalizeId(user?.id || user?._id || user?.userId);

    let matched = false;
    const updated = enrollments.map(e => {
      const storedCourseId = normalizeId(e.courseId);
      const storedUserId = normalizeId(e.userId || e.user);
      
      const isMatch = storedCourseId === targetCourseId && (!currentUserId || !storedUserId || storedUserId === currentUserId);
      if (isMatch) {
        matched = true;
        const currentCompleted = Array.isArray(e.completedLessons) ? e.completedLessons.map(normalizeId) : [];
        const isAlreadyDone = currentCompleted.includes(targetLessonId);
        const newCompleted = isAlreadyDone ? currentCompleted : [...currentCompleted, targetLessonId];

        return { 
          ...e, 
          completedLessons: newCompleted,
          lastLessonId: targetLessonId 
        };
      }
      return e;
    });

    if (!matched) {
      updated.push({
        courseId: targetCourseId,
        userId: currentUserId,
        enrolledAt: new Date().toISOString(),
        completedLessons: [targetLessonId],
        quizResults: [],
        courseCompleted: false,
        lastLessonId: targetLessonId
      });
    }

    saveToLocal(updated);

    // Optional background sync with backend
    if (isAuthenticated) {
      try {
        await completeLectureApi(targetCourseId, targetLessonId);
      } catch (e) {
        console.warn("Complete lecture background sync notice:", e.message);
      }
    }
  }, [enrollments, user, isAuthenticated, saveToLocal]);

  const isLessonCompleted = useCallback((courseId, lessonId) => {
    if (!courseId || !lessonId) return false;
    const e = getEnrollment(courseId);
    if (!e || !Array.isArray(e.completedLessons)) return false;
    const targetLessonId = normalizeId(lessonId);
    return e.completedLessons.some(id => normalizeId(id) === targetLessonId);
  }, [getEnrollment]);

  const saveQuizResult = useCallback((result) => {
    if (!result || !result.courseId) return;
    const targetCourseId = normalizeId(result.courseId);
    const currentUserId = normalizeId(user?.id || user?._id || user?.userId);
    const targetLessonId = result.lessonId ? normalizeId(result.lessonId) : null;

    let matched = false;
    const updated = enrollments.map(e => {
      const storedCourseId = normalizeId(e.courseId);
      const storedUserId = normalizeId(e.userId || e.user);

      const isMatch = storedCourseId === targetCourseId && (!currentUserId || !storedUserId || storedUserId === currentUserId);
      if (isMatch) {
        matched = true;
        const results = Array.isArray(e.quizResults) ? e.quizResults : [];
        const existingIndex = results.findIndex(r => 
          (r.quizId && result.quizId && normalizeId(r.quizId) === normalizeId(result.quizId)) || 
          (r.lessonId && targetLessonId && normalizeId(r.lessonId) === targetLessonId) ||
          (r.lectureId && targetLessonId && normalizeId(r.lectureId) === targetLessonId)
        );
        const newResults = existingIndex >= 0 
          ? results.map((r, i) => i === existingIndex ? result : r)
          : [...results, result];
        
        const completed = Array.isArray(e.completedLessons) ? e.completedLessons.map(normalizeId) : [];
        const newCompleted = (result.passed && targetLessonId && !completed.includes(targetLessonId))
          ? [...completed, targetLessonId]
          : completed;

        const isCourseNowCompleted = e.courseCompleted || (result.isFinalQuiz && result.passed);

        return { 
          ...e, 
          quizResults: newResults,
          completedLessons: newCompleted,
          lastLessonId: targetLessonId || e.lastLessonId,
          courseCompleted: isCourseNowCompleted,
          completedAt: isCourseNowCompleted ? (e.completedAt || new Date().toISOString()) : e.completedAt
        };
      }
      return e;
    });

    if (!matched) {
      updated.push({
        courseId: targetCourseId,
        userId: currentUserId,
        enrolledAt: new Date().toISOString(),
        completedLessons: (result.passed && targetLessonId) ? [targetLessonId] : [],
        quizResults: [result],
        courseCompleted: Boolean(result.isFinalQuiz && result.passed),
        lastLessonId: targetLessonId,
        completedAt: (result.isFinalQuiz && result.passed) ? new Date().toISOString() : null
      });
    }

    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const getQuizResult = useCallback((quizOrLessonId) => {
    if (!quizOrLessonId) return undefined;
    const searchId = normalizeId(quizOrLessonId);
    for (const e of enrollments) {
      if (!Array.isArray(e.quizResults)) continue;
      const r = e.quizResults.find(r => 
        normalizeId(r.quizId) === searchId || 
        normalizeId(r.lessonId) === searchId ||
        normalizeId(r.lectureId) === searchId
      );
      if (r) return r;
    }
    return undefined;
  }, [enrollments]);

  const setLastLesson = useCallback((courseId, lessonId) => {
    if (!courseId) return;
    const targetCourseId = normalizeId(courseId);
    const targetLessonId = normalizeId(lessonId);
    const currentUserId = normalizeId(user?.id || user?._id || user?.userId);

    const updated = enrollments.map(e => {
      const storedCourseId = normalizeId(e.courseId);
      const storedUserId = normalizeId(e.userId || e.user);
      if (storedCourseId === targetCourseId && (!currentUserId || !storedUserId || storedUserId === currentUserId)) {
        return { ...e, lastLessonId: targetLessonId };
      }
      return e;
    });
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const completeCourse = useCallback((courseId) => {
    if (!courseId) return;
    const targetCourseId = normalizeId(courseId);
    const currentUserId = normalizeId(user?.id || user?._id || user?.userId);

    const updated = enrollments.map(e => {
      const storedCourseId = normalizeId(e.courseId);
      const storedUserId = normalizeId(e.userId || e.user);
      if (storedCourseId === targetCourseId && (!currentUserId || !storedUserId || storedUserId === currentUserId)) {
        return { 
          ...e, 
          courseCompleted: true, 
          completedAt: new Date().toISOString() 
        };
      }
      return e;
    });
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  /**
   * Calculates the real-time progress percentage (0 - 100) for a given course.
   * Handles total lessons count gracefully.
   */
  const getLessonProgress = useCallback((courseId, totalLessons = 0) => {
    if (!courseId) return 0;
    const e = getEnrollment(courseId);
    if (!e) return 0;

    // If marked completed, 100%
    if (e.courseCompleted) return 100;

    const completedCount = Array.isArray(e.completedLessons) ? e.completedLessons.length : 0;
    
    if (totalLessons > 0) {
      const calculated = Math.round((completedCount / totalLessons) * 100);
      return Math.min(100, Math.max(0, calculated));
    }

    if (typeof e.progress === 'number' && e.progress >= 0) {
      return Math.min(100, Math.round(e.progress));
    }

    return completedCount > 0 ? 100 : 0;
  }, [getEnrollment]);

  return (
    <ProgressContext.Provider value={{
      enrollments,
      loading,
      refreshEnrollments,
      enroll,
      isEnrolled,
      completeLesson,
      isLessonCompleted,
      saveQuizResult,
      getQuizResult,
      getEnrollment,
      setLastLesson,
      completeCourse,
      getLessonProgress,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within ProgressProvider');
  return ctx;
}